'use client';

import React, { useState } from 'react';
import { Booking, createBooking, formatPrice, formatDuration } from '@/lib/bookingStore';
import { AgendarState } from './AgendarPage';

interface Props {
  agendarState: AgendarState;
  onConfirm: (booking: Booking) => void;
  onBack: () => void;
}

export default function AgendarStepSummary({ agendarState, onConfirm, onBack }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { selectedServices, selectedDate, selectedTime, customerName, customerWhatsapp, customerNotes } = agendarState;

  const totalPrice = selectedServices.reduce((sum, s) => sum + s.price, 0);
  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration, 0);

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-').map(Number);
    const months = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
    const days = ['Domingo','Segunda-feira','Terça-feira','Quarta-feira','Quinta-feira','Sexta-feira','Sábado'];
    const date = new Date(y, m - 1, d);
    return `${days[date.getDay()]}, ${d} de ${months[m - 1]} de ${y}`;
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      const result = createBooking({
        customerName,
        customerWhatsapp,
        customerNotes,
        serviceIds: selectedServices.map(s => s.id),
        date: selectedDate,
        startTime: selectedTime,
      });
      if (result.success && result.booking) {
        onConfirm(result.booking);
      } else {
        setError(result.error || 'Erro ao criar marcação. Por favor, tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <span className="gold-line mb-4 block" />
        <h2 className="font-serif text-3xl lg:text-4xl font-light text-dark-brown mb-2">
          Rever marcação
        </h2>
        <p className="text-sm text-foreground/60">Confirme os detalhes antes de finalizar.</p>
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        {/* Services */}
        <div className="p-6 border-b border-border">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-foreground/50 mb-3">Serviços</p>
          <div className="space-y-2">
            {selectedServices.map(s => (
              <div key={s.id} className="flex items-center justify-between">
                <span className="text-sm text-foreground">{s.name}</span>
                <span className="text-sm font-semibold text-foreground tabular-nums">{formatPrice(s.price)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Date & Time */}
        <div className="p-6 border-b border-border grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-foreground/50 mb-1">Data</p>
            <p className="text-sm text-foreground font-medium">{formatDateDisplay(selectedDate)}</p>
          </div>
          <div>
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-foreground/50 mb-1">Horário</p>
            <p className="text-sm text-foreground font-medium">{selectedTime}</p>
          </div>
        </div>

        {/* Customer */}
        <div className="p-6 border-b border-border">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-foreground/50 mb-3">Cliente</p>
          <p className="text-sm text-foreground font-medium">{customerName}</p>
          <p className="text-sm text-foreground/70">{customerWhatsapp}</p>
          {customerNotes && <p className="text-xs text-foreground/50 mt-1 italic">"{customerNotes}"</p>}
        </div>

        {/* Total */}
        <div className="p-6 bg-ivory">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-foreground/60">Duração total</span>
            <span className="text-sm font-medium text-foreground">{formatDuration(totalDuration)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-foreground">Total</span>
            <span className="font-bold text-2xl text-burnt-rose tabular-nums">{formatPrice(totalPrice)}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-danger-bg border border-danger/20 rounded-xl p-4 animate-slide-up">
          <p className="text-sm text-danger font-medium">{error}</p>
          {error.includes('horário') && (
            <button onClick={onBack} className="text-xs text-danger underline mt-1">
              Escolher outro horário
            </button>
          )}
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={onBack}
          disabled={loading}
          className="flex-1 py-4 rounded-full border-2 border-border text-sm font-semibold tracking-wide uppercase text-foreground/60 hover:border-burnt-rose hover:text-burnt-rose transition-all duration-200 disabled:opacity-50"
        >
          Voltar
        </button>
        <button
          onClick={handleConfirm}
          disabled={loading}
          className="flex-grow-[2] py-4 rounded-full btn-primary font-semibold text-sm tracking-[0.1em] uppercase disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? 'A confirmar...' : 'Confirmar Marcação'}
        </button>
      </div>
    </div>
  );
}
